<template>
    <inline-tab-panel
        name="timeline"
        v-bind="$attrs"
        class="timeline-tab-panel"
        with-variable-height
    >
        <template v-for="(eventLogsForDate, date) in groupedEventLogs" :key="date">
            <div class="timeline-tab-panel__date">
                <text-decorator :size="FontSize.sm" bold>
                    {{ date }}
                </text-decorator>
            </div>

            <div class="timeline-tab-panel__feed">
                <div
                    v-for="eventLog in eventLogsForDate"
                    :key="eventLog.id.toString()"
                    class="timeline-tab-panel__feed-item"
                >
                    <span
                        v-if="!isLastEventLog(eventLog)"
                        class="timeline-tab-panel__feed-item__connector"
                        aria-hidden="true"
                    />

                    <div class="timeline-tab-panel__feed-item__content-container">
                        <div class="timeline-tab-panel__feed-item__icon" :style="{ backgroundColor: eventIconBackgroundColor(eventLog) }">
                            <svg-icon
                                :icon="eventIcon(eventLog)"
                                :size="IconSize.md"
                                :color="eventIconColor(eventLog)"
                            />
                        </div>

                        <div class="timeline-tab-panel__feed-item__content">
                            <div class="timeline-tab-panel__feed-item__event">
                                <div>
                                    <text-decorator :size="FontSize.sm" bold>
                                        {{ eventLabel(eventLog) }}
                                    </text-decorator>
                                </div>
                                <div>
                                    <text-decorator :size="FontSize.sm" :color="Color.gray400">
                                        {{ formatDateTime(eventLog.loggedAt) }}
                                    </text-decorator>
                                </div>
                            </div>

                            <div class="timeline-tab-panel__feed-item__user">
                                <text-decorator :size="FontSize.sm" :color="Color.gray400">
                                    Door:
                                </text-decorator>

                                <div v-if="isUserEventLog(eventLog)">
                                    <user-avatar
                                        :placeholder="eventLog.loggedBy?.abbreviation ?? ''"
                                        :url="eventLog.loggedBy?.avatarUrl ? resizedToSizeUploadUrl(eventLog.loggedBy.avatarUrl, 'lg') : ''"
                                        :alt="eventLog.loggedBy?.name ?? '' "
                                    />
                                    <text-decorator :size="FontSize.sm">
                                        {{ eventLog.loggedBy?.name ?? 'Onbekend' }}
                                    </text-decorator>
                                </div>

                                <div v-else-if="isVirtualColleagueEventLog(eventLog)">
                                    <virtual-colleague-avatar
                                        size="lg"
                                        :variant="currentLegacyCompany.virtualColleagueAvatar as any"
                                    />
                                    <flex-container
                                        direction="column"
                                        size="auto"
                                        gap="xs"
                                    >
                                        <text-decorator :size="FontSize.sm">
                                            {{ currentLegacyCompany.virtualColleagueName }}
                                        </text-decorator>
                                        <text-decorator :color="Color.gray400" :size="FontSize.xs">
                                            Virtuele Collega
                                        </text-decorator>
                                    </flex-container>
                                </div>

                                <div v-else-if="isSystemEventLog(eventLog)">
                                    <keyvue-text-logo style="height: 20px;" />
                                </div>
                            </div>

                            <div v-if="false" class="timeline-tab-panel__feed-item__comment">
                                En hier dan een comment als er wat te zeggen valt over de gebeurtenis.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </inline-tab-panel>
</template>

<script setup lang="ts">
    import {
        Color,
        IconSize,
        InlineTabPanel,
        TextDecorator,
        UserAvatar,
        SvgIcon,
        FontSize,
        VirtualColleagueAvatar,
    } from '@keyvue/one-ui';

    import IconAddTask from '@keyvue/one-material-icons/icons/add_task.svg?component';
    import IconAltRoute from '@keyvue/one-material-icons/icons/alt_route.svg?component';
    import IconCall from '@keyvue/one-material-icons/icons/call.svg?component';
    import IconCancel from '@keyvue/one-material-icons/icons/cancel.svg?component';
    import IconChannelOnline from '@assets/icons/channels/ChannelOnline.svg?component';
    import IconChannelRealworks from '@assets/icons/channels/ChannelRealworks.svg?component';
    import IconChannelUser from '@assets/icons/channels/ChannelUser.svg?component';
    import IconCheckCircle from '@keyvue/one-material-icons/icons/check_circle.svg?component';
    import IconContactPage from '@keyvue/one-material-icons/icons/contact_page.svg?component';
    import IconEditCalendar from '@keyvue/one-material-icons/icons/edit_calendar.svg?component';
    import IconFilterAlt from '@keyvue/one-material-icons/icons/filter_alt.svg?component';
    import IconGroup from '@keyvue/one-material-icons/icons/group.svg?component';
    import IconLineStartCircle from '@keyvue/one-material-icons/icons/line_start_circle.svg?component';
    import IconLocationHome from '@keyvue/one-material-icons/icons/location_home.svg?component';
    import IconOtherHouses from '@keyvue/one-material-icons/icons/other_houses.svg?component';
    import IconOutgoingMail from '@keyvue/one-material-icons/icons/outgoing_mail.svg?component';
    import IconPersonAdd from '@keyvue/one-material-icons/icons/person_add.svg?component';
    import IconPhoneIphone from '@keyvue/one-material-icons/icons/phone_iphone.svg?component';
    import IconReadinessScore from '@keyvue/one-material-icons/icons/readiness_score.svg?component';
    import IconSupervisorAccount from '@keyvue/one-material-icons/icons/supervisor_account.svg?component';
    import IconAlternateEmail from '@keyvue/one-material-icons/icons/alternate_email.svg?component';

    import KeyvueTextLogo from '@assets/logo-text.svg?component';

    import { formatDateTime } from '@/utils/date';
    import { resizedToSizeUploadUrl } from '@/utils/resize';
    import { translateLegacyStatus, translateLegacyStatusReason } from '@/utils/legacy';

    import { Channel } from '@keyvue/one-api-client';
    import type { EventLog } from '@keyvue/one-api-client-legacy';
    import { computed } from 'vue';
    import useClient from '@composables/useClient';

    interface Props {
        eventLogs: EventLog[];
    }

    const props = defineProps<Props>();
    const { currentLegacyCompany } = useClient();

    const groupedEventLogs = computed(() => {
        const eventLogsByDate = {};

        const formatDateHeading = (date: Date) => new Intl.DateTimeFormat('nl-NL', {
            dateStyle: 'long',
        }).format(date);

        props.eventLogs.forEach((eventLog) => {
            const date = new Date(eventLog.loggedAt);
            const formattedDate = formatDateHeading(date);

            if (!(formattedDate in eventLogsByDate)) {
                eventLogsByDate[formattedDate] = [];
            }

            eventLogsByDate[formattedDate].push(eventLog);
        });

        return eventLogsByDate as Record<string, EventLog[]>;
    });

    const eventIcon = (eventLog: EventLog) => {
        const mapping = {
            ContactCreatedEvent: IconContactPage,
            ContactEmailChangedEvent: IconAlternateEmail,
            ContactMobilePhonenumberChangedEvent: IconPhoneIphone,
            ContactPhonenumberChangedEvent: IconCall,
            ContactSpouseChangedEvent: IconGroup,
            ReferralCreatedEvent: IconPersonAdd,
            ReferralAssignedToBranchChangedEvent: IconLocationHome,
            ReferralAssignedToChangedEvent: IconSupervisorAccount,
            ReferralStatusChangedEvent: IconAltRoute,
            ReferralPhaseChangedEvent: IconFilterAlt,
            ReferralFutureAddressChangedEvent: IconOtherHouses,
            ReferralProductionDateChangedEvent: IconEditCalendar,
            ReferralScoreChangedEvent: IconReadinessScore,
            ReferralSourceChangedEvent: IconLineStartCircle,
            ActionCreatedEvent: IconAddTask,
            ActionIsCompletedChangedEvent: IconCheckCircle,
            EmailSent: IconOutgoingMail,
        };

        if (eventLog.event in mapping) {
            return mapping[eventLog.event];
        }

        if (eventLog.event === 'ReferralChannelChangedEvent') {
            const channelIconMap = {
                [Channel.USER.value]: IconChannelUser,
                [Channel.ONLINE.value]: IconChannelOnline,
                [Channel.REALWORKS.value]: IconChannelRealworks,
            };

            return channelIconMap[eventLog.eventData.channel];
        }

        if (eventLog.event === 'ActionIsCompletedChangedEvent') {
            if (eventLog.eventData.isCompleted) {
                return IconCheckCircle;
            }

            return IconCancel;
        }

        return null;
    };

    const eventIconBackgroundColor = (eventLog: EventLog) => {
        const mapping = {
            ContactCreatedEvent: '#ADD8E6',
            ContactEmailChangedEvent: '#ADD8E6',
            ContactMobilePhonenumberChangedEvent: '#ADD8E6',
            ContactPhonenumberChangedEvent: '#ADD8E6',
            ContactSpouseChangedEvent: '#ADD8E6',
            ReferralCreatedEvent: '#98FB98',
            ReferralAssignedToBranchChangedEvent: '#98FB98',
            ReferralAssignedToChangedEvent: '#98FB98',
            ReferralStatusChangedEvent: '#98FB98',
            ReferralPhaseChangedEvent: '#98FB98',
            ReferralFutureAddressChangedEvent: '#DDA0DD',
            ReferralProductionDateChangedEvent: '#DDA0DD',
            ReferralScoreChangedEvent: '#FFB266',
            ReferralChannelChangedEvent: '#DDA0DD',
            ReferralSourceChangedEvent: '#DDA0DD',
            ActionCreatedEvent: '#FFB6C1',
            ActionIsCompletedChangedEvent: '#FFB266',
            EmailSent: '#FFB6C1',
        };

        if (eventLog.event in mapping) {
            return mapping[eventLog.event];
        }

        return 'inherit';
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const eventIconColor = (eventLog: EventLog) => Color.white;

    const eventLabel = (eventLog: EventLog) => {
        const mapping = {
            ContactCreatedEvent: 'Binnengekomen als nieuw contact',
            ContactEmailChangedEvent: 'E-mailadres gewijzigd',
            ContactMobilePhonenumberChangedEvent: 'Mobiele telefoonnummer gewijzigd',
            ContactPhonenumberChangedEvent: 'Telefoonnummer gewijzigd',
            ContactSpouseChangedEvent: 'Partnergegevens gewijzigd',
            ReferralCreatedEvent: 'Lead aangemaakt',
            ReferralAssignedToBranchChangedEvent: 'Toegewezen aan ander team',
            ReferralAssignedToChangedEvent: 'Toegewezen aan andere gebruiker',
            ReferralFutureAddressChangedEvent: 'Toekomstig adres aangepast',
            ReferralProductionDateChangedEvent: 'Productiedatum aangepast',
            ReferralScoreChangedEvent: 'Kans aangepast',
            ActionCreatedEvent: 'Actie aangemaakt',
            EmailSentEvent: 'Er is een e-mail verstuurd',
        };

        if (eventLog.event in mapping) {
            return mapping[eventLog.event];
        }

        if (eventLog.event === 'ReferralStatusChangedEvent') {
            let status = translateLegacyStatus(eventLog.eventData.status);

            if (eventLog.eventData.statusReason) {
                status += ` (${translateLegacyStatusReason(eventLog.eventData.statusReason)})`;
            }

            if (eventLog.eventData.newStatusReason && eventLog.eventData.status !== 'open') {
                status += ` (${eventLog.eventData.newStatusReason})`;
            }

            return `Status aangepast naar "${status}"`;
        }

        if (eventLog.event === 'ReferralPhaseChangedEvent') {
            let { phase } = eventLog.eventData;

            if (eventLog.eventData.newStatusReason) {
                phase += ` (${eventLog.eventData.newStatusReason})`;
            }

            return `Fase aangepast naar "${phase}"`;
        }

        if (eventLog.event === 'ReferralChannelChangedEvent') {
            const channel = new Channel(eventLog.eventData.channel);

            return `Kanaal aangepast naar "${channel.label}"`;
        }

        if (eventLog.event === 'ReferralSourceChangedEvent') {
            const { source } = eventLog.eventData;

            return `Bron aangepast naar "${source}"`;
        }

        if (eventLog.event === 'ActionIsCompletedChangedEvent') {
            if (eventLog.eventData.isCompleted) {
                return 'Actie gemarkeerd als voltooid';
            }

            return 'Actie gemarkeerd als nog niet voltooid';
        }

        return eventLog.event;
    };

    const isLastEventLog = (eventLog: EventLog) => {
        const lastEventLog = props.eventLogs[props.eventLogs.length - 1];

        return eventLog.id === lastEventLog.id;
    };

    const originMapping = computed(() => ({
        ContactCreatedEvent: 'virtual_colleague',
        ContactEmailChangedEvent: 'virtual_colleague',
        ContactMobilePhonenumberChangedEvent: 'virtual_colleague',
        ContactPhonenumberChangedEvent: 'virtual_colleague',
        ContactSpouseChangedEvent: 'virtual_colleague',
        ReferralCreatedEvent: 'virtual_colleague',
        ReferralAssignedToBranchChangedEvent: 'virtual_colleague',
        ReferralAssignedToChangedEvent: 'virtual_colleague',
        ReferralStatusChangedEvent: 'virtual_colleague',
        ReferralPhaseChangedEvent: 'virtual_colleague',
        ReferralFutureAddressChangedEvent: 'virtual_colleague',
        ReferralProductionDateChangedEvent: 'virtual_colleague',
        ReferralScoreChangedEvent: 'virtual_colleague',
        ReferralSourceChangedEvent: 'virtual_colleague',
        ActionCreatedEvent: 'virtual_colleague',
        ActionIsCompletedChangedEvent: 'virtual_colleague',
        EmailSent: 'system',
    }));

    const isUserEventLog = (eventLog: EventLog) => eventLog.loggedBy;
    const isSystemEventLog = (eventLog: EventLog) => !isUserEventLog(eventLog);
    const isVirtualColleagueEventLog = (eventLog: EventLog) => !isUserEventLog(eventLog) && originMapping.value[eventLog.event] === 'virtual_colleague';
</script>

<style scoped lang="scss">
.timeline-tab-panel {
    padding: size(md) !important;

    &__date {
        margin-bottom: size(md);
        color: color(gray-400);
    }

    &__feed {
        padding-bottom: size(md);
    }

    &__feed-item {
        margin-bottom: size(md);
        padding-bottom: size(md);
        display: flex;
        flex-direction: column;
        gap: size(sm);
        position: relative;

        &__connector {
            position: absolute;
            top: calc(icon-size(md) / 2 + size(xs)); // 50% of icon width + left padding
            left: calc(icon-size(md) / 2 + size(xs) - 1px); // 50% of icon width + left padding - 50% of border width
            height: 100%;
            width: 2px;
            background-color: color(gray-300);
        }

        &__content-container {
            display: flex;
            align-items: flex-start;
            align-content: flex-start;
            gap: size(sm);
        }

        &__icon {
            position: relative;

            display: grid;
            grid-template-rows: 1fr;

            padding: size(xs);
            background-color: color(gray-300);
            border: none;
            border-radius: 100%;
            box-shadow: 0 0 0 size(sm) color(brand-white);
        }

        &__content {
            display: grid;
            gap: size(sm);
        }

        &__event {
            display: grid;
            gap: 2px;
        }

        &__user {
            display: grid;
            gap: size(xs);

            > div {
                display: flex;
                align-items: center;
                gap: size(sm);
            }

            &__logo > svg {
                height: size(md);
            }
        }

        &__comment {
            width: fit-content;
            background-color: color(gray-200);
            padding: size(md);
            border-radius: size(sm);
            border-top-left-radius: unset;
            line-height: size(md);
        }
    }
}
</style>
